
import { useCallback } from 'react';
import { newsSources } from '../../../../mocks/localNews';

const getSource = (id: string) => newsSources.find((s) => s.id === id);

interface Article {
  id: string;
  title: string;
  category: string;
  source: string;
  excerpt: string;
  summary?: string;
  url: string;
  date: string;
}

export default function PrintButton({ article }: { article: Article }) {
  const src = getSource(article.source);
  const content = article.summary || article.excerpt;

  const handlePrint = useCallback(() => {
    // Try to open a new window; if it fails (popup blocker) alert the user.
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Unable to open print preview. Please allow pop‑ups for this site.');
      return;
    }

    // Safely format the date; fall back to the raw string if parsing fails.
    let formattedDate: string;
    try {
      formattedDate = new Date(article.date).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      formattedDate = article.date;
    }

    // Split content into readable paragraphs, ensuring each chunk ends with a period.
    const paragraphs = content
      .split('. ')
      .reduce<string[]>((acc, sentence, idx, arr) => {
        const lastGroup = acc[acc.length - 1] || '';
        const combined = lastGroup ? `${lastGroup}. ${sentence}` : sentence;

        if (combined.length > 400 && lastGroup) {
          // Close the previous paragraph and start a new one.
          acc[acc.length - 1] = `${lastGroup}.`;
          acc.push(sentence);
        } else {
          acc[acc.length - 1] = combined;
        }

        // Ensure the final paragraph ends with a period.
        if (idx === arr.length - 1 && !acc[acc.length - 1].endsWith('.')) {
          acc[acc.length - 1] = `${acc[acc.length - 1]}.`;
        }
        return acc;
      }, ['']);

    // Write the printable HTML.
    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <title>${article.title} - Roseville.org</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&family=Inter:wght@400;500;600&display=swap');
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Merriweather', Georgia, serif;
            color: #1a1a1a;
            max-width: 700px;
            margin: 0 auto;
            padding: 40px 24px;
            line-height: 1.8;
          }
          .header {
            border-bottom: 3px solid #1a1a1a;
            padding-bottom: 16px;
            margin-bottom: 24px;
          }
          .site-name {
            font-family: 'Inter', sans-serif;
            font-size: 14px;
            font-weight: 600;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 2px;
            margin-bottom: 4px;
          }
          .meta {
            font-family: 'Inter', sans-serif;
            display: flex;
            gap: 16px;
            flex-wrap: wrap;
            margin-bottom: 16px;
          }
          .meta span {
            font-size: 12px;
            color: #666;
          }
          .category {
            display: inline-block;
            font-family: 'Inter', sans-serif;
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #0d9488;
            margin-bottom: 12px;
          }
          h1 {
            font-size: 28px;
            line-height: 1.3;
            margin-bottom: 16px;
            font-weight: 700;
          }
          .content p {
            font-size: 15px;
            margin-bottom: 16px;
            text-align: justify;
          }
          .source-box {
            margin-top: 32px;
            padding: 16px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-family: 'Inter', sans-serif;
            font-size: 12px;
            color: #666;
          }
          .source-box strong { color: #1a1a1a; }
          .footer {
            margin-top: 32px;
            padding-top: 16px;
            border-top: 1px solid #ddd;
            font-family: 'Inter', sans-serif;
            font-size: 11px;
            color: #999;
            text-align: center;
          }
          @media print {
            body { padding: 0; }
            .no-print { display: none !important; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="site-name">Roseville.org &mdash; Local News</div>
        </div>
        <span class="category">${article.category}</span>
        <h1>${article.title}</h1>
        <div class="meta">
          <span>${formattedDate}</span>
          <span>Source: ${src?.name || 'Roseville.org'}</span>
        </div>
        <div class="content">
          ${paragraphs.map((p) => `<p>${p}</p>`).join('')}
        </div>
        <div class="source-box">
          <strong>Original Source:</strong> ${src?.name || 'Roseville.org'}<br/>
          <strong>Full Article:</strong> ${article.url}
        </div>
        <div class="footer">
          Printed from Roseville.org &mdash; Your comprehensive guide to Roseville, California<br/>
          &copy; ${new Date().getFullYear()} Roseville.org. All rights reserved.
        </div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    // Close the document to finish loading.
    printWindow.document.close();
  }, [article, content, src]);

  return (
    <button
      onClick={handlePrint}
      className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
      title="Print this article"
    >
      <i className="ri-printer-line text-lg"></i>
      Print Article
    </button>
  );
}
